import React, { useEffect, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

// Container for close button
const CloseButtonContainer = styled.div`
  position: fixed;
  bottom: 60px;
  right: 15px;
  z-index: 1000010; /* Higher than Tawk widget */
  display: ${props => props.isVisible ? 'block' : 'none'};
  pointer-events: all;
`;

const CloseButton = styled.button`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.8);
  color: #555;
  border: 1px solid rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.2s ease;
  padding: 0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  
  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }
  
  &:focus {
    outline: none;
  }
`;

// Global styled component for custom Tawk button styles
const CustomTawkStyles = createGlobalStyle`
  /* Style the chat button and add text */
  .tawk-button-circle.tawk-button-large::before {
    content: 'Live Chat';
    position: absolute;
    left: -85px; /* Position text to the left of the button */
    bottom: 10px;
    background: #2e7d32;
    color: white;
    padding: 8px 12px;
    border-radius: 20px;
    font-weight: bold;
    font-size: 14px;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
    white-space: nowrap;
  }
  
  /* Add a bit of spacing to the right of the text to separate from the icon */
  .tawk-chat-button-hover {
    border-radius: 20px 50% 50% 20px !important;
    width: auto !important;
    padding-left: 70px !important;
  }
`;

// Global CSS to forcefully hide the bubble
const HideBubbleStyle = createGlobalStyle`
  /* Hide the "We Are Here" bubble and hand - super specific targeting */
  .tawk-bubble, 
  .tawk-bubble-container,
  #tawk-bubble-container,
  .tawk-text-center.tawk-padding-small,
  .tawk-button-circle.tawk-custom-color.tawk-custom-border-color,
  iframe[title="chat widget"],
  iframe[id^="im"][title="chat widget"],
  iframe[style*="bottom:30px"][style*="right:0px"][title="chat widget"],
  iframe[style*="position:fixed"][style*="bottom:30px"],
  body > iframe[style*="bottom:30px"][style*="position:fixed"],
  iframe[style*="min-height:95px"][style*="min-width:124px"],
  /* Extremely specific selector to match the exact iframe */
  iframe[style*="position:fixed"][style*="bottom:30px"][style*="right:0px"][style*="min-height:95px"] {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
    width: 0 !important;
    height: 0 !important;
    max-width: 0 !important;
    max-height: 0 !important;
    min-width: 0 !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
    z-index: -9999 !important;
  }
`;

const TawkChat = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [tawkLoaded, setTawkLoaded] = useState(false);
  
  // Retrieve visibility state from localStorage on component mount
  useEffect(() => {
    // Force visibility on for development/testing
    setIsVisible(true);
    localStorage.setItem('tawkChatVisible', 'true');
    
    // Check if we need to restore visibility based on time
    const hiddenUntil = localStorage.getItem('tawkChatHiddenUntil');
    if (hiddenUntil && parseInt(hiddenUntil) < Date.now()) {
      // Time has passed, show button again
      setIsVisible(true);
      localStorage.removeItem('tawkChatHiddenUntil');
      localStorage.setItem('tawkChatVisible', 'true');
    }
  }, []);
  
  const handleClose = () => {
    // When close is clicked, hide both the X button and the chat widget
    setIsVisible(false);
    
    // Hide Tawk widget if it's loaded
    if (window.Tawk_API && typeof window.Tawk_API.hideWidget === 'function') {
      window.Tawk_API.hideWidget();
    }
    
    // Save preference to localStorage
    localStorage.setItem('tawkChatVisible', 'false');
    
    // Set a timeout to show the button again after some time (e.g., 7 days)
    const expiryTime = Date.now() + (7 * 24 * 60 * 60 * 1000); // 7 days in milliseconds
    localStorage.setItem('tawkChatHiddenUntil', expiryTime.toString());
  };
  
  useEffect(() => {
    // Configure Tawk API before loading script
    var Tawk_API = window.Tawk_API || {};
    var Tawk_LoadStart = new Date();
    
    // Method 1: Configure options before widget loads
    Tawk_API.onBeforeLoad = function() {
      // Show or hide based on user preference
      if (!isVisible && typeof Tawk_API.hideWidget === 'function') {
        Tawk_API.hideWidget();
      }
      // Set to not show the bubble
      Tawk_API.showBubble = false;
      
      // Apply custom styling
      Tawk_API.customStyle = {
        zIndex: 1000,
        visibility: {
          desktop: {
            position: 'br',
            xOffset: 20,
            yOffset: 20,
            bubble: false, // Disable bubble
          },
          mobile: {
            position: 'br',
            xOffset: 10, 
            yOffset: 10,
            bubble: false, // Disable bubble
          }
        }
      };
    };
    
    // Method 2: After load, use direct DOM manipulation
    Tawk_API.onLoad = function() {
      setTawkLoaded(true);
      
      // Show or hide based on user preference
      if (!isVisible && typeof Tawk_API.hideWidget === 'function') {
        Tawk_API.hideWidget();
      }
      const removeBubble = () => {
        // Target all potential bubble elements by various selectors
        const bubbleSelectors = [
          '.tawk-bubble', 
          '.tawk-bubble-container',
          '#tawk-bubble-container',
          '[data-testid="bubbleText"]',
          '.tawk-button-circle',
          '.tawk-min-container'
        ];
        
        // Find and remove all matching elements
        bubbleSelectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          elements.forEach(el => {
            if (el && el.parentNode) {
              el.parentNode.removeChild(el);
            }
          });
        });

        // Directly target all chat widget iframes with specific attributes
        document.querySelectorAll('iframe[title="chat widget"]').forEach(iframe => {
          // Check if this is the bubble iframe (not the main chat widget)
          if (iframe.style.bottom === '30px' || 
              iframe.id.startsWith('im') || 
              (iframe.style.height && iframe.style.height.includes('95px'))) {
            if (iframe && iframe.parentNode) {
              iframe.parentNode.removeChild(iframe);
            }
          }
        });
      };
      
      // Try multiple times to catch after elements are rendered
      removeBubble();
      setTimeout(removeBubble, 100);
      setTimeout(removeBubble, 500);
      setTimeout(removeBubble, 1000);
      setTimeout(removeBubble, 2000);
      
      // Also set up a mutation observer to catch any dynamically added elements
      const observer = new MutationObserver((mutations) => {
        removeBubble();
      });
      
      // Start observing the document body for changes
      observer.observe(document.body, { 
        childList: true, 
        subtree: true 
      });
    };
    
    // Load the Tawk.to script
    const s1 = document.createElement('script');
    const s0 = document.getElementsByTagName('script')[0];
    
    s1.async = true;
    s1.src = 'https://embed.tawk.to/686eca4cca9b1d190e69d344/1ivoccef6';
    s1.charset = 'UTF-8';
    s1.setAttribute('crossorigin', '*');
    
    if (s0 && s0.parentNode) {
      s0.parentNode.insertBefore(s1, s0);
    } else {
      document.head.appendChild(s1);
    }
    
    // Method 3: Add direct CSS to hide bubble
    const style = document.createElement('style');
    style.innerHTML = `
      .tawk-bubble, 
      .tawk-bubble-container,
      #tawk-bubble-container,
      .tawk-min-container,
      .tawk-text-center.tawk-padding-small,
      .tawk-button-circle {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);
    
    // Cleanup function
    return () => {
      if (document.contains(s1)) {
        s1.parentNode.removeChild(s1);
      }
      if (document.contains(style)) {
        style.parentNode.removeChild(style);
      }
      delete window.Tawk_API;
      delete window.Tawk_LoadStart;
    };
  }, []);

  return (
    <>
      <HideBubbleStyle />
      <CustomTawkStyles />
      <CloseButtonContainer isVisible={isVisible}>
        <CloseButton onClick={handleClose} aria-label="Close chat button">
          <FontAwesomeIcon icon={faTimes} size="sm" />
        </CloseButton>
      </CloseButtonContainer>
    </>
  );
};

export default TawkChat;
